import type { ParsedUrlQuery } from 'querystring';
import { LOCATION_CHANGE } from 'connected-react-router';
import type { SagaIterator } from 'redux-saga';
import { call, takeEvery } from 'redux-saga/effects';
import { SOURCE_PARAM } from '@members/on-demand-modal/constants';
import { ModalActionType } from './redux';
import { MODAL_PARAM } from './urls';

export const modalCloseSaga = function* (
  action: any, // Using any type to unblock type erorrs on router.replace calls,
): SagaIterator {
  const queryParams = { ...action.payload.router.query };

  yield call(action.payload.router.push, {
    query: toQueryParamsWithoutModal(queryParams, action.payload.additionalParams),
  });
};

// NOTE: This saga is intentionally a no-op.
//
// When connected-react-router fires LOCATION_CHANGE, the modalReducer already
// handles it: it reads ?modal= from the URL, sets `name`, and marks
// `openedVia = 'url'`.  Previously this saga re-dispatched openModal() which
// overwrote openedVia to 'action'.  That caused ConnectedModal to read the
// (never-cleared) Redux name even after the modal URL param was removed, so
// the modal stayed open on locale-prefixed pages where LOCATION_CHANGE doesn't
// fire on subsequent SPA navigations.
export const modalOpenSaga = function* (): SagaIterator {
  // Intentional no-op — see comment above.
};

// In Next.js static export mode, hard-navigating to a locale-prefixed URL (e.g.
// /de-DE/classes/all) causes router.query to contain template-string placeholders
// for dynamic path params (e.g. categorySlug: ['[...categorySlug]']). Filter
// these out so they don't end up as literal strings in the navigated URL.
const isNextjsTemplateValue = (value: ParsedUrlQuery[string]): boolean => {
  // Match only actual Next.js route template strings like [param] or [...param],
  // not JSON-encoded filter values like ["id1","id2"] which also start/end with brackets.
  const isNextJSTemplate = (v: string): boolean =>
    /^(?:\[(?:\w+|\.\.\.\w+)\]|\[\[\.\.\.\w+\]\])$/.test(v);
  if (Array.isArray(value)) {
    return value.every(v => typeof v === 'string' && isNextJSTemplate(v));
  }
  return typeof value === 'string' && isNextJSTemplate(value);
};

export const toQueryParamsWithoutModal = (
  queryParams: ParsedUrlQuery,
  additionalModalParams: string[] = [],
) => {
  const paramsToOmit = [MODAL_PARAM, SOURCE_PARAM, ...additionalModalParams];
  const filteredParams = Object.keys(queryParams)
    .filter(param => !paramsToOmit.includes(param))
    .filter(param => !isNextjsTemplateValue(queryParams[param]))
    .reduce((obj, param) => {
      return {
        ...obj,
        [param]: queryParams[param],
      };
    }, {});

  return filteredParams;
};

export const modalWatcherSaga = function* () {
  yield takeEvery(ModalActionType.Close, modalCloseSaga);
  yield takeEvery(LOCATION_CHANGE, modalOpenSaga);
};
